import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Minus,
  Plus,
  TicketPercent,
  BadgePercent,
  ChevronsRight,
  Box,
  MapPin,
  Trash2,
  Truck,
  Store,
  Bus,
  Check,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";
import { inter, philosopher } from "@/lib/fonts";
import {
  IApplyCouponResponse,
  IAddUserAddressInput,
  ICoupon,
  ICartItem,
  IProductVariant,
  IUserAddress,
  priceFormat,
  calculateSellerDeliveryCharge,
  getApplicableBulkPrice,
  getAvailableStock,
  getUnitPrice,
  OrderDeliveryType,
  MAX_ORDER_REMARK_LENGTH,
} from "muminsmart-utils";
import {
  useApplyCouponMutation,
  useAddWishlistItemMutation,
  useCreateUserAddressMutation,
  useDeleteUserAddressMutation,
  useGetCartItemsQuery,
  useGetUserAddressesQuery,
  useLazyGetValidCouponsQuery,
  useRemoveCartItemMutation,
  useUpdateCartItemMutation,
  useUpdateUserAddressMutation,
} from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { useShowInitialLoader } from "@/lib/hooks";
import { toast } from "sonner";
import { useSession } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { routes } from "@/lib/constants";
import CartSkeleton, {
  AddressSkeleton,
} from "@/app/components/common/skeletons/CartSkeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AddressFormModal from "./AddressFormModal";
import AddressListDrawer from "./AddressListDrawer";
import CouponListDrawer from "./CouponListDrawer";
import Checkout from "./Checkout";
import CustomImage from "../common/Image";
import { cn } from "@/lib/utils";
import { useCartWishlist } from "@/lib/hooks/useCartWishlist";

const deliveryTypeLabels: Record<
  OrderDeliveryType,
  { Icon: LucideIcon; label: string }
> = {
  [OrderDeliveryType.Courier]: { Icon: Truck, label: "Courier to Doorstep" },
  [OrderDeliveryType.Pickup]: { Icon: Store, label: "Pickup from Outlet" },
  [OrderDeliveryType.Transport]: { Icon: Bus, label: "Send to Transport" },
};

const EMPTY_ADDRESS_FORM: IAddUserAddressInput = {
  label: "",
  fullName: "",
  phone: "+91",
  senderName: "",
  senderPhone: "+91",
  addressLine1: "",
  addressLine2: "",
  preferredCourier: "",
  preferredTransport: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  isDefault: false,
};

function getVariantLabel(variant: IProductVariant) {
  const values = (variant.variantAttributes || [])
    .map((item) => item.value)
    .filter(Boolean);

  return values.join(" | ") || variant.sku;
}

function getSellerDisplayName(items: ICartItem[]) {
  const firstVariant = items[0]?.productVariant;

  return (
    firstVariant?.seller?.name ||
    firstVariant?.product?.seller?.name ||
    "Seller"
  );
}

function getSellerAddress(items: ICartItem[]) {
  const firstVariant = items[0]?.productVariant;
  const seller = firstVariant?.seller || firstVariant?.product?.seller;

  if (!seller) {
    return null;
  }

  const parts = [
    seller.addressLine1,
    seller.addressLine2,
    seller.city,
    seller.state,
    seller.postalCode,
    seller.country,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : null;
}

function getDeliveryHint(group: {
  deliveryType: OrderDeliveryType;
  transportCharge: number;
  totalWeight: number;
}) {
  if (group.deliveryType === OrderDeliveryType.Pickup) {
    return "No delivery charges for pickup. Enter your preferred pickup outlet in remarks.";
  }

  if (group.deliveryType === OrderDeliveryType.Transport) {
    return `Fixed transport charge: ${priceFormat(group.transportCharge)}. Onward charges are paid on delivery.`;
  }

  const remainingKg = Math.ceil(group.totalWeight) - group.totalWeight;

  if (remainingKg > 0.05) {
    return `Add ${remainingKg.toFixed(1)} kg more for the same shipping charge.`;
  }

  return "Courier shipping is billed by this seller’s total weight.";
}

function getCouponButtonLabel(
  isCouponLoading: boolean,
  hasAppliedCoupon: boolean,
) {
  if (isCouponLoading) {
    return "APPLYING...";
  }

  if (hasAppliedCoupon) {
    return "REMOVE";
  }

  return "APPLY";
}

export default function CartPage() {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] =
    useState<IApplyCouponResponse | null>(null);
  const [couponErrorMessage, setCouponErrorMessage] = useState<string | null>(
    null,
  );
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState<ICoupon[]>([]);
  const [isCouponDrawerOpen, setIsCouponDrawerOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null,
  );
  const [isAddressDrawerOpen, setIsAddressDrawerOpen] = useState(false);
  const [isAddressFormDrawerOpen, setIsAddressFormDrawerOpen] = useState(false);
  const [isAddressMutating, setIsAddressMutating] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [addressForm, setAddressForm] =
    useState<IAddUserAddressInput>(EMPTY_ADDRESS_FORM);
  const [sellerDeliveryTypes, setSellerDeliveryTypes] = useState<
    Record<string, OrderDeliveryType>
  >({});
  const [sellerRemarks, setSellerRemarks] = useState<Record<string, string>>(
    {},
  );
  const [expandedSellerDetails, setExpandedSellerDetails] = useState<
    Record<string, boolean>
  >({});
  const [isOutOfStockDialogOpen, setIsOutOfStockDialogOpen] = useState(false);
  const [checkoutOutOfStockVariantIds, setCheckoutOutOfStockVariantIds] =
    useState<string[]>([]);
  const [isMovingOutOfStockItems, setIsMovingOutOfStockItems] = useState(false);
  const { status } = useSession();
  const navigate = useNavigate();
  const { isWishlisted } = useCartWishlist();
  const skip = status !== "authenticated";
  const {
    data: cartData,
    isLoading: isCartLoading,
    refetch: refetchCartItems,
  } = useGetCartItemsQuery(undefined, { skip });
  const {
    data: addressData,
    isLoading: isAddressQueryLoading,
    refetch: refetchAddresses,
  } = useGetUserAddressesQuery(undefined, { skip });
  const items = useMemo(() => cartData ?? [], [cartData]);
  const addresses = useMemo(() => addressData ?? [], [addressData]);
  const showCartLoader = useShowInitialLoader(isCartLoading, cartData);
  const showAddressLoader = useShowInitialLoader(
    isAddressQueryLoading,
    addressData,
  );
  const [removeCartItem] = useRemoveCartItemMutation();
  const [updateCartItem] = useUpdateCartItemMutation();
  const [createUserAddress] = useCreateUserAddressMutation();
  const [updateUserAddress] = useUpdateUserAddressMutation();
  const [deleteUserAddress] = useDeleteUserAddressMutation();
  const [applyCoupon] = useApplyCouponMutation();
  const [addWishlistItem] = useAddWishlistItemMutation();
  const [fetchValidCoupons, { isFetching: isCouponListLoading }] =
    useLazyGetValidCouponsQuery();

  useEffect(() => {
    if (skip || !cartData?.length) {
      return;
    }

    const hasIncompleteItems = cartData.some(
      (item) => !item.productVariant?.product,
    );

    if (hasIncompleteItems) {
      void refetchCartItems();
    }
  }, [cartData, refetchCartItems, skip]);

  useEffect(() => {
    if (!addressData?.length) {
      setSelectedAddressId(null);
      return;
    }

    setSelectedAddressId((previousId) => {
      if (
        previousId &&
        addressData.some((address) => address.id === previousId)
      ) {
        return previousId;
      }

      const defaultAddress =
        addressData.find((address) => address.isDefault) || addressData[0];

      return defaultAddress?.id || null;
    });
  }, [addressData]);

  const selectedAddress = useMemo(() => {
    if (!addresses.length) {
      return null;
    }

    return (
      addresses.find((address) => address.id === selectedAddressId) ||
      addresses.find((address) => address.isDefault) ||
      addresses[0]
    );
  }, [addresses, selectedAddressId]);

  const refreshAddresses = async () => {
    await refetchAddresses();
  };

  const openAddAddressDrawer = () => {
    setEditingAddressId(null);
    setAddressForm(EMPTY_ADDRESS_FORM);
    setIsAddressFormDrawerOpen(true);
  };

  const openEditAddressDrawer = (address: IUserAddress) => {
    setEditingAddressId(address.id);
    setAddressForm({
      label: address.label || "",
      fullName: address.fullName,
      phone: address.phone,
      senderName: address.senderName || "",
      senderPhone: address.senderPhone || "",
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || "",
      preferredCourier: address.preferredCourier || "",
      preferredTransport: address.preferredTransport || "",
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      country: address.country,
      isDefault: address.isDefault,
    });
    setIsAddressDrawerOpen(false);
    setIsAddressFormDrawerOpen(true);
  };

  const submitAddress = async (values: IAddUserAddressInput) => {
    const payload: IAddUserAddressInput = {
      label: values.label?.trim() || undefined,
      fullName: values.fullName.trim(),
      phone: values.phone.trim(),
      senderName: values.senderName?.trim() || undefined,
      senderPhone: values.senderPhone?.trim() || undefined,
      addressLine1: values.addressLine1.trim(),
      addressLine2: values.addressLine2?.trim() || undefined,
      preferredCourier: values.preferredCourier?.trim() || undefined,
      preferredTransport: values.preferredTransport?.trim() || undefined,
      city: values.city.trim(),
      state: values.state.trim(),
      pincode: values.pincode.trim(),
      country: values.country.trim() || "India",
      isDefault: Boolean(values.isDefault),
    };

    setIsAddressMutating(true);

    try {
      if (editingAddressId) {
        const data = await updateUserAddress({
          id: editingAddressId,
          data: payload,
        }).unwrap();

        setSelectedAddressId(data.id);
        toast.success("Address updated");
      } else {
        const data = await createUserAddress(payload).unwrap();

        setSelectedAddressId(data.id);
        toast.success("Address added");
      }

      await refreshAddresses();
      setIsAddressFormDrawerOpen(false);
      setEditingAddressId(null);
      setAddressForm(EMPTY_ADDRESS_FORM);
    } catch (error: unknown) {
      toast.error(getRtkQueryErrorMessage(error, "Failed to save address"));
    } finally {
      setIsAddressMutating(false);
    }
  };

  const deleteAddress = async (addressId: string) => {
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this address?",
    );

    if (!shouldDelete) {
      return;
    }

    setIsAddressMutating(true);

    try {
      await deleteUserAddress(addressId).unwrap();

      toast.success("Address deleted");
      await refreshAddresses();
    } catch (error: unknown) {
      toast.error(getRtkQueryErrorMessage(error, "Failed to delete address"));
    } finally {
      setIsAddressMutating(false);
    }
  };

  const selectAddress = async (address: IUserAddress) => {
    setSelectedAddressId(address.id);
    setIsAddressDrawerOpen(false);

    // if (address.isDefault) {
    //   return;
    // }

    // setIsAddressMutating(true);

    // try {
    //   const response = await patchItem<IUserAddress, Record<string, never>>(
    //     `${endpoints.userAddress.id(address.id)}/default`,
    //     {},
    //   );

    //   if (!response.success || !response.data) {
    //     toast.error(response.message || "Failed to set default address");
    //     return;
    //   }

    //   await refreshAddresses();
    // } finally {
    //   setIsAddressMutating(false);
    // }
  };

  const subtotal = useMemo(
    () =>
      items.reduce((sum, item) => {
        const variant = item.productVariant;
        if (!variant) {
          return sum;
        }

        return sum + getUnitPrice(variant, item.quantity) * item.quantity;
      }, 0),
    [items],
  );

  const totalEligibleQuantity = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );

  const lowestEligibleUnitPrice = useMemo(() => {
    const prices = items
      .map((item) => {
        const variant = item.productVariant;
        if (!variant) {
          return null;
        }

        return getUnitPrice(variant, item.quantity);
      })
      .filter((value): value is number => value !== null);

    if (!prices.length) {
      return undefined;
    }

    return Math.min(...prices);
  }, [items]);

  const savingsFromMRP = useMemo(
    () =>
      items.reduce((sum, item) => {
        const variant = item.productVariant;
        if (!variant) {
          return sum;
        }

        return (
          sum +
          (variant.price - getUnitPrice(variant, item.quantity)) * item.quantity
        );
      }, 0),
    [items],
  );

  const sellerGroups = useMemo(() => {
    const groups = new Map<string, ICartItem[]>();

    for (const item of items) {
      const sellerId = item.productVariant?.sellerId || "unknown-seller";
      const existing = groups.get(sellerId) || [];
      existing.push(item);
      groups.set(sellerId, existing);
    }

    return Array.from(groups.entries()).map(([sellerId, sellerItems]) => {
      const sellerSubtotal = sellerItems.reduce((sum, item) => {
        const variant = item.productVariant;

        if (!variant) {
          return sum;
        }

        return sum + getUnitPrice(variant, item.quantity) * item.quantity;
      }, 0);

      const sellerTotalWeight = sellerItems.reduce((sum, item) => {
        const variant = item.productVariant;
        if (!variant) {
          return sum;
        }

        return sum + Number(variant.weight || 0) * item.quantity;
      }, 0);

      const sellerShippingPerKg = Number(
        sellerItems[0]?.productVariant?.seller?.shippingCharge ?? 0,
      );
      const sellerTransportCharge = Number(
        sellerItems[0]?.productVariant?.seller?.transportCharge ?? 0,
      );
      const selectedDeliveryType =
        sellerDeliveryTypes[sellerId] ?? OrderDeliveryType.Courier;

      const shippingCharge = calculateSellerDeliveryCharge(
        selectedDeliveryType,
        sellerShippingPerKg,
        sellerTotalWeight,
        sellerTransportCharge,
      );

      return {
        sellerId,
        sellerName: getSellerDisplayName(sellerItems),
        sellerAddress: getSellerAddress(sellerItems),
        items: sellerItems,
        deliveryType: selectedDeliveryType,
        totalWeight: sellerTotalWeight,
        shippingPerKg: sellerShippingPerKg,
        transportCharge: sellerTransportCharge,
        subtotal: sellerSubtotal,
        shippingCharge,
        total: sellerSubtotal + shippingCharge,
      };
    });
  }, [items, sellerDeliveryTypes]);

  const checkoutOutOfStockItems = useMemo(() => {
    const outOfStockVariantIds = new Set(checkoutOutOfStockVariantIds);

    return items.filter((item) =>
      item.productVariantId
        ? outOfStockVariantIds.has(item.productVariantId)
        : false,
    );
  }, [checkoutOutOfStockVariantIds, items]);

  useEffect(() => {
    setSellerDeliveryTypes((previous) => {
      const next: Record<string, OrderDeliveryType> = {};

      for (const group of sellerGroups) {
        next[group.sellerId] =
          previous[group.sellerId] ?? OrderDeliveryType.Courier;
      }

      const previousKeys = Object.keys(previous);
      const nextKeys = Object.keys(next);

      if (
        previousKeys.length === nextKeys.length &&
        nextKeys.every((key) => previous[key] === next[key])
      ) {
        return previous;
      }

      return next;
    });
  }, [sellerGroups]);

  useEffect(() => {
    setSellerRemarks((previous) => {
      const next: Record<string, string> = {};

      for (const group of sellerGroups) {
        next[group.sellerId] = previous[group.sellerId] ?? "";
      }

      const previousKeys = Object.keys(previous);
      const nextKeys = Object.keys(next);

      if (
        previousKeys.length === nextKeys.length &&
        nextKeys.every((key) => previous[key] === next[key])
      ) {
        return previous;
      }

      return next;
    });
  }, [sellerGroups]);

  useEffect(() => {
    setExpandedSellerDetails((previous) => {
      const next: Record<string, boolean> = {};

      for (const group of sellerGroups) {
        next[group.sellerId] = previous[group.sellerId] ?? false;
      }

      const previousKeys = Object.keys(previous);
      const nextKeys = Object.keys(next);

      if (
        previousKeys.length === nextKeys.length &&
        nextKeys.every((key) => previous[key] === next[key])
      ) {
        return previous;
      }

      return next;
    });
  }, [sellerGroups]);

  const couponDiscount = appliedCoupon?.discountAmount ?? 0;
  const totalShippingCharge = useMemo(
    () => sellerGroups.reduce((sum, group) => sum + group.shippingCharge, 0),
    [sellerGroups],
  );
  const totalSavings = savingsFromMRP + couponDiscount;
  const orderTotal = Math.max(
    0,
    subtotal + totalShippingCharge - couponDiscount,
  );

  const handleApplyCoupon = async (codeOverride?: string) => {
    const codeToApply = (codeOverride ?? couponCode).trim().toUpperCase();

    if (!codeToApply) {
      toast.error("Please enter a coupon code");
      return;
    }

    if (!items.length) {
      toast.error("Cart is empty");
      return;
    }

    setIsCouponLoading(true);
    setCouponErrorMessage(null);

    try {
      const data = await applyCoupon({
        code: codeToApply,
        orderAmount: subtotal,
        eligibleQuantity: totalEligibleQuantity,
        eligibleUnitPrice: lowestEligibleUnitPrice,
        consume: false,
      }).unwrap();

      setAppliedCoupon(data);
      setCouponCode(codeToApply);
      setCouponErrorMessage(null);
    } catch (error: unknown) {
      setAppliedCoupon(null);
      setCouponErrorMessage(
        getRtkQueryErrorMessage(error, "Failed to apply coupon"),
      );
    } finally {
      setIsCouponLoading(false);
    }
  };

  const loadValidCoupons = useCallback(async () => {
    try {
      const data = await fetchValidCoupons().unwrap();
      setAvailableCoupons(data);
    } catch {
      toast.error("Failed to fetch coupons");
      setAvailableCoupons([]);
    }
  }, [fetchValidCoupons]);

  useEffect(() => {
    if (!isCouponDrawerOpen || status !== "authenticated") {
      return;
    }

    loadValidCoupons().catch(() => undefined);
  }, [isCouponDrawerOpen, status, loadValidCoupons]);

  const handleApplyCouponFromDrawer = (code: string) => {
    setIsCouponDrawerOpen(false);
    setAppliedCoupon(null);
    setCouponErrorMessage(null);
    void handleApplyCoupon(code);
  };

  const updateQuantity = async (item: ICartItem, quantity: number) => {
    if (quantity < 1) {
      return;
    }

    const variant = item.productVariant;
    if (variant && quantity > getAvailableStock(variant)) {
      toast.error("Selected quantity is not available in stock");
      return;
    }

    setActiveItemId(item.id);

    try {
      await updateCartItem({
        id: item.id,
        quantity,
      }).unwrap();

      setAppliedCoupon(null);
    } catch {
      toast.error("Failed to update cart item");
    } finally {
      setActiveItemId(null);
    }
  };

  const remove = async (id: string) => {
    setActiveItemId(id);

    try {
      await removeCartItem(id).unwrap();

      setAppliedCoupon(null);
    } catch {
      toast.error("Failed to remove cart item");
    } finally {
      setActiveItemId(null);
    }
  };

  const inc = (item: ICartItem) => updateQuantity(item, item.quantity + 1);

  const dec = (item: ICartItem) => updateQuantity(item, item.quantity - 1);

  const handleDismissOutOfStockDialog = () => {
    setIsOutOfStockDialogOpen(false);
    setCheckoutOutOfStockVariantIds([]);
  };

  const moveItemsToWishlist = async (itemsToMove: ICartItem[]) => {
    if (!itemsToMove.length || isMovingOutOfStockItems) {
      return;
    }

    setIsMovingOutOfStockItems(true);

    try {
      for (const item of itemsToMove) {
        const variant = item.productVariant;
        const product = variant?.product;

        if (!variant?.id || !product?.id) {
          continue;
        }

        if (!isWishlisted(variant.id)) {
          await addWishlistItem({
            productId: product.id,
            productVariantId: variant.id,
          }).unwrap();
        }

        await removeCartItem(item.id).unwrap();
      }

      setAppliedCoupon(null);
      setIsOutOfStockDialogOpen(false);
      setCheckoutOutOfStockVariantIds([]);
      toast.success(
        itemsToMove.length > 1
          ? "Items moved to wishlist"
          : "Item moved to wishlist",
      );
    } catch (error: unknown) {
      toast.error(
        getRtkQueryErrorMessage(error, "Failed to move items to wishlist"),
      );
    } finally {
      setIsMovingOutOfStockItems(false);
    }
  };

  const handleCheckoutOutOfStock = (productVariantIds: string[]) => {
    setCheckoutOutOfStockVariantIds(productVariantIds);
    setIsOutOfStockDialogOpen(true);
  };

  return (
    <>
      <ButtonsHeader heading="My Cart" />
      {items.length > 0 && (
        <div className="flex items-center justify-center rounded-t-3xl bg-[#E5F5E5] py-2 text-emerald-700">
          <BadgePercent className="h-4 bg-[#E5F5E5]" />
          <p className={`text-sm font-semibold ${inter.className}`}>
            You saved {priceFormat(totalSavings)} on this order
          </p>
        </div>
      )}
      <div className="space-y-4">
        {/* Cart Items */}
        <div className="space-y-4 bg-white px-3 py-4 sm:px-4 sm:py-5 md:px-6">
          {showCartLoader && <CartSkeleton />}
          {!showCartLoader && items.length === 0 && (
            <div className="py-16 text-center text-sm text-gray-500">
              Your cart is empty.
            </div>
          )}
          {!showCartLoader &&
            items.length > 0 &&
            sellerGroups.map((group) => {
              const isDetailsExpanded =
                expandedSellerDetails[group.sellerId] ?? false;
              const hasRemark = Boolean(sellerRemarks[group.sellerId]?.trim());

              return (
                <Card
                  key={group.sellerId}
                  className="overflow-hidden border border-zinc-200/80 rounded-2xl bg-white shadow-xs p-0 gap-0"
                >
                  {/* Seller Header */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 bg-[#FAF4EA]/80 px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100/70 text-amber-800">
                        <Store className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3
                            className={`text-base font-semibold text-zinc-900 ${philosopher.className}`}
                          >
                            {group.sellerName}
                          </h3>
                          <span
                            className={`text-[11px] font-medium text-amber-800 bg-amber-100/60 px-2 py-0.5 rounded-full ${inter.className}`}
                          >
                            Seller
                          </span>
                        </div>
                        <p
                          className={`text-xs text-muted-foreground ${inter.className}`}
                        >
                          {group.items.length} item
                          {group.items.length > 1 ? "s" : ""}
                          {group.totalWeight > 0
                            ? ` • ${group.totalWeight.toFixed(2)} kg total`
                            : ""}
                        </p>
                      </div>
                    </div>

                    <Link
                      to={routes.seller.id(group.sellerId)}
                      className={`inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline ${inter.className}`}
                    >
                      View Shop
                    </Link>
                  </div>

                  {/* Seller Products */}
                  <div className="divide-y divide-zinc-100 px-3 sm:px-4">
                    {group.items.map((item) => {
                      const variant = item.productVariant;
                      const product = variant?.product;
                      const unitPrice = variant
                        ? getUnitPrice(variant, item.quantity)
                        : 0;
                      const bulkPrice = variant
                        ? getApplicableBulkPrice(
                            variant.bulkPrices,
                            item.quantity,
                          )
                        : null;
                      const isMutating = activeItemId === item.id;
                      const productDetailsPath =
                        product && variant
                          ? routes.products.id(product.id, variant.id)
                          : null;
                      const effectiveStock = variant
                        ? getAvailableStock(variant)
                        : Infinity;
                      const isOutOfStock = effectiveStock <= 0;

                      return (
                        <div
                          key={item.id}
                          className="py-3.5 flex items-stretch gap-3 sm:gap-4"
                        >
                          <button
                            type="button"
                            className="relative w-20 min-h-24 self-stretch overflow-hidden rounded-lg sm:w-24 sm:min-h-31 disabled:cursor-default shrink-0 border border-zinc-100 bg-zinc-50"
                            onClick={() => {
                              if (!productDetailsPath) {
                                return;
                              }

                              navigate(productDetailsPath);
                            }}
                            disabled={!productDetailsPath}
                            aria-label={
                              product?.name ||
                              variant?.sku ||
                              "View product details"
                            }
                          >
                            <CustomImage
                              src={variant?.featuredImage || ""}
                              alt={product?.name || variant?.sku || "Cart item"}
                              className="object-contain"
                              fill
                            />
                          </button>

                          <div className="flex w-full flex-1 flex-col justify-between">
                            <div>
                              <h4
                                className={`line-clamp-2 text-sm font-semibold leading-tight sm:text-base ${philosopher.className}`}
                              >
                                {product?.name || variant?.sku || "Product"}
                              </h4>
                              <p
                                className={`mt-1 text-xs text-muted-foreground ${inter.className}`}
                              >
                                {variant ? getVariantLabel(variant) : "-"}
                                {typeof bulkPrice?.price === "number" && (
                                  <>
                                    <span className="mx-1">|</span>Per Piece
                                    Price - {priceFormat(bulkPrice.price)}
                                  </>
                                )}
                              </p>
                              <div className="mt-2 flex items-center gap-2">
                                <span
                                  className={`text-base font-semibold ${inter.className}`}
                                >
                                  {priceFormat(unitPrice)}
                                </span>
                                {variant?.price && variant.price > unitPrice ? (
                                  <span
                                    className={`text-xs text-muted-foreground line-through ${inter.className}`}
                                  >
                                    {priceFormat(variant.price)}
                                  </span>
                                ) : null}
                              </div>
                            </div>

                            <div className="mt-3 flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void remove(item.id);
                                  }}
                                  disabled={isMutating}
                                  className={`rounded-full p-2 border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm inline-flex items-center gap-1 font-semibold transition-colors ${inter.className}`}
                                  aria-label="Remove item"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                                {isOutOfStock ? (
                                  <button
                                    type="button"
                                    disabled={isMovingOutOfStockItems}
                                    className={`rounded-full border border-primary px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/5 disabled:opacity-60 ${inter.className}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void moveItemsToWishlist([item]);
                                    }}
                                  >
                                    Move to Wishlist
                                  </button>
                                ) : null}
                              </div>
                              {!isOutOfStock ? (
                                <div className="inline-flex items-center rounded-full border border-zinc-200 bg-white">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      dec(item).catch(() => undefined);
                                    }}
                                    disabled={isMutating || item.quantity <= 1}
                                    className={cn(
                                      "grid place-items-center size-9",
                                      item.quantity === 1
                                        ? "text-gray-300"
                                        : "text-zinc-700 hover:bg-zinc-100 rounded-l-full",
                                    )}
                                    aria-label="Decrease quantity"
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                  <span
                                    className={`px-1 text-sm font-medium select-none ${inter.className}`}
                                  >
                                    <Input
                                      type="number"
                                      className="text-center text-sm font-semibold w-11 h-8 border-none p-0 focus-visible:ring-0"
                                      value={item.quantity}
                                      min={1}
                                      max={effectiveStock}
                                      onChange={(event) => {
                                        const newQuantity = Number(
                                          event.target.value,
                                        );

                                        if (
                                          Number.isNaN(newQuantity) ||
                                          newQuantity < 1 ||
                                          newQuantity > effectiveStock
                                        ) {
                                          return;
                                        }

                                        void updateQuantity(item, newQuantity);
                                      }}
                                    />
                                  </span>
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      inc(item).catch(() => undefined);
                                    }}
                                    disabled={
                                      isMutating ||
                                      item.quantity >= effectiveStock
                                    }
                                    className={cn(
                                      "grid place-items-center size-9",
                                      item.quantity >= effectiveStock
                                        ? "text-gray-300"
                                        : "text-zinc-700 hover:bg-zinc-100 rounded-r-full",
                                    )}
                                    aria-label="Increase quantity"
                                  >
                                    <Plus className="size-4" />
                                  </button>
                                </div>
                              ) : (
                                <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
                                  Out of Stock
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Clubbed Delivery Section */}
                  <div
                    className={`border-t border-zinc-100 bg-zinc-50/70 p-3.5 sm:p-4 ${inter.className} space-y-3`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-600">
                        <Truck className="h-3.5 w-3.5 text-primary" />
                        Delivery Method
                      </div>
                    </div>

                    <RadioGroup
                      value={
                        sellerDeliveryTypes[group.sellerId] ||
                        OrderDeliveryType.Courier
                      }
                      onValueChange={(value: OrderDeliveryType) => {
                        setSellerDeliveryTypes((previous) => ({
                          ...previous,
                          [group.sellerId]: value,
                        }));
                      }}
                      className="grid grid-cols-3 gap-2 sm:gap-3"
                    >
                      {Object.entries(deliveryTypeLabels).map(
                        ([deliveryType, { Icon, label }]) => {
                          const typedDeliveryType =
                            deliveryType as OrderDeliveryType;
                          const radioId = `${group.sellerId}-${deliveryType}`;
                          const isSelected =
                            (sellerDeliveryTypes[group.sellerId] ||
                              OrderDeliveryType.Courier) === typedDeliveryType;

                          return (
                            <Label
                              key={radioId}
                              className={cn(
                                "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border bg-white p-2.5 text-center transition-all",
                                isSelected
                                  ? "border-primary bg-primary/5 text-primary shadow-xs font-medium"
                                  : "border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50/50",
                              )}
                            >
                              <RadioGroupItem
                                id={radioId}
                                value={deliveryType}
                                aria-label={label}
                                className="absolute inset-0 z-10 size-auto h-full w-full border-0 opacity-0 shadow-none focus-visible:ring-0"
                              />
                              {isSelected && (
                                <span className="pointer-events-none absolute top-1.5 right-1.5 z-20 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white">
                                  <Check
                                    className="h-2.5 w-2.5"
                                    strokeWidth={3}
                                  />
                                </span>
                              )}
                              <Icon
                                className="shrink-0"
                                size={22}
                                strokeWidth={1.5}
                              />
                              <span className="text-[11px] font-medium leading-tight sm:text-xs">
                                {label}
                              </span>
                            </Label>
                          );
                        },
                      )}
                    </RadioGroup>
                    {/* Delivery Hint */}
                    <div className="rounded-lg bg-amber-50/60 p-2 px-3 text-[11px] leading-relaxed text-amber-800 sm:text-xs border border-amber-100/80">
                      {getDeliveryHint(group)}
                    </div>
                    {/* Pickup Address details when Pickup is selected */}
                    {group.deliveryType === OrderDeliveryType.Pickup && (
                      <div className="rounded-xl border border-amber-200/80 bg-amber-50/40 p-3 text-xs space-y-1">
                        <div className="flex items-center gap-1.5 font-semibold text-amber-900">
                          <MapPin className="h-3.5 w-3.5 shrink-0 text-amber-700" />
                          <span>Pickup Outlet Address</span>
                        </div>
                        <p className="text-zinc-700 leading-relaxed pl-5">
                          {group.sellerAddress || "Outlet address unavailable"}
                        </p>
                      </div>
                    )}

                    <button
                      type="button"
                      aria-expanded={isDetailsExpanded}
                      aria-label={
                        isDetailsExpanded
                          ? "Hide remark and order summary"
                          : "Show remark and order summary"
                      }
                      className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-left"
                      onClick={() => {
                        setExpandedSellerDetails((previous) => ({
                          ...previous,
                          [group.sellerId]: !isDetailsExpanded,
                        }));
                      }}
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-zinc-800">
                          Remark & order summary
                        </p>
                        {!isDetailsExpanded && hasRemark ? (
                          <p className="mt-0.5 text-[11px] text-primary">
                            Remark added
                          </p>
                        ) : null}
                      </div>
                      <div className="ml-3 flex shrink-0 items-center gap-2">
                        {!isDetailsExpanded ? (
                          <span className="text-xs font-semibold text-zinc-900">
                            {priceFormat(group.total)}
                          </span>
                        ) : null}
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 text-zinc-500 transition-transform duration-300",
                            isDetailsExpanded ? "rotate-180" : "rotate-0",
                          )}
                        />
                      </div>
                    </button>

                    <div
                      className={cn(
                        "grid transition-[grid-template-rows,opacity] duration-300 ease-in-out",
                        isDetailsExpanded
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0",
                      )}
                    >
                      <div className="space-y-3 overflow-hidden">
                        <div className="space-y-2 p-1">
                          <Label
                            htmlFor={`remark-${group.sellerId}`}
                            className="text-xs font-semibold uppercase tracking-wider text-zinc-600"
                          >
                            Order Remark (Optional)
                          </Label>
                          <Textarea
                            id={`remark-${group.sellerId}`}
                            placeholder="Add a note for this seller (e.g. delivery instructions, gift message)"
                            value={sellerRemarks[group.sellerId] ?? ""}
                            maxLength={MAX_ORDER_REMARK_LENGTH}
                            rows={2}
                            className="resize-none bg-white text-xs rounded-xl"
                            onChange={(event) => {
                              setSellerRemarks((previous) => ({
                                ...previous,
                                [group.sellerId]: event.target.value,
                              }));
                            }}
                          />
                        </div>

                        {/* Seller Subtotal, Shipping Charges, & Seller Total */}
                        <div className="space-y-2 rounded-xl bg-white p-3 border border-zinc-200/70 text-xs text-zinc-600">
                          <div className="flex items-center justify-between">
                            <span>Seller subtotal</span>
                            <span className="font-medium text-zinc-800">
                              {priceFormat(group.subtotal)}
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-3">
                            <span>Shipping charges</span>
                            <span className="max-w-[70%] text-right text-[11px] font-medium text-zinc-800 sm:text-xs">
                              {group.deliveryType === OrderDeliveryType.Pickup
                                ? `Pick Up — ${priceFormat(0)}`
                                : null}
                              {group.deliveryType ===
                              OrderDeliveryType.Transport
                                ? `Transport Charge — ${priceFormat(group.transportCharge)}`
                                : null}
                              {group.deliveryType === OrderDeliveryType.Courier
                                ? `${priceFormat(group.shippingPerKg)}/kg × ${group.totalWeight.toFixed(2)} kg = ${priceFormat(group.shippingCharge)}`
                                : null}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm font-semibold text-zinc-900">
                            <span>Seller total</span>
                            <span>{priceFormat(group.total)}</span>
                          </div>
                          {group.deliveryType ===
                          OrderDeliveryType.Transport ? (
                            <p className="mt-1.5 text-[11px] text-amber-700 leading-tight">
                              *Transport charges include delivery to the
                              transport provider only. Any onward transport
                              charges are to be paid by the customer upon
                              delivery.
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
        </div>

        {/* Saving Corner */}
        {items.length > 0 && (
          <section className="bg-white px-4 py-4 sm:px-6 sm:py-5 md:px-8">
            <div className="flex items-center gap-2 px-1">
              <div className="grid place-items-center rounded-xl p-1.5 text-amber-700 bg-[#FDF2E7]">
                <TicketPercent className="h-4 w-4" />
              </div>
              <h2 className={`text-xl font-semibold ${philosopher.className}`}>
                Saving Corner
              </h2>
            </div>

            <Card className="mt-3 rounded-2xl py-3">
              <CardContent
                className={`flex flex-col items-start justify-between gap-4 p-4 sm:flex-row sm:items-center ${inter.className}`}
              >
                <div>
                  <p className="text-md font-semibold">Have a coupon code?</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Apply & save more.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      disabled={Boolean(appliedCoupon)}
                      onChange={(event) => {
                        setCouponCode(event.target.value.toUpperCase());
                        if (couponErrorMessage) {
                          setCouponErrorMessage(null);
                        }
                      }}
                    />
                  </div>
                  {appliedCoupon && (
                    <p className="mt-2 text-xs text-green-700">
                      {appliedCoupon.code} applied. You saved{" "}
                      {priceFormat(appliedCoupon.discountAmount)}.
                    </p>
                  )}
                  {!appliedCoupon && couponErrorMessage && (
                    <p className="mt-2 text-xs text-red-600">
                      {couponErrorMessage}
                    </p>
                  )}
                </div>
                <Button
                  variant={appliedCoupon ? "secondary" : "default"}
                  disabled={isCouponLoading}
                  className="mt-1 w-full rounded-full sm:mt-3 sm:w-auto"
                  onClick={() => {
                    if (appliedCoupon) {
                      setAppliedCoupon(null);
                      setCouponErrorMessage(null);
                      return;
                    }

                    void handleApplyCoupon();
                  }}
                >
                  {getCouponButtonLabel(
                    isCouponLoading,
                    Boolean(appliedCoupon),
                  )}
                </Button>
              </CardContent>
            </Card>

            <div className={inter.className}>
              <CouponListDrawer
                open={isCouponDrawerOpen}
                onOpenChange={setIsCouponDrawerOpen}
                coupons={availableCoupons}
                isLoading={isCouponListLoading}
                isApplying={isCouponLoading}
                appliedCouponCode={appliedCoupon?.code}
                onApplyCoupon={handleApplyCouponFromDrawer}
              />
            </div>
          </section>
        )}

        {/* Addresses */}
        <section className="bg-white px-4 py-4 sm:px-6 sm:py-5 md:px-8">
          <div className="flex items-center gap-2 px-1">
            <div className="grid place-items-center rounded-xl p-1.5 text-amber-700 bg-[#FDF2E7]">
              <Box className="h-4 w-4" />
            </div>
            <h2 className={`text-xl font-semibold ${philosopher.className}`}>
              Delivery Addresses
            </h2>
          </div>

          <Card className="mt-3 rounded-2xl py-3">
            <CardContent className={`p-4 gap-4 ${inter.className}`}>
              {showAddressLoader && <AddressSkeleton />}
              {!showAddressLoader && selectedAddress && (
                <div className="w-full space-y-3">
                  <div className="rounded-xl border border-zinc-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-zinc-900">
                            {selectedAddress.label || "Saved Address"}
                          </p>
                          {selectedAddress.isDefault && (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-zinc-800">
                          {selectedAddress.fullName} · +{selectedAddress.phone}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedAddress.addressLine1}
                          {selectedAddress.addressLine2
                            ? `, ${selectedAddress.addressLine2}`
                            : ""}
                          , {selectedAddress.city}, {selectedAddress.state} -{" "}
                          {selectedAddress.pincode}, {selectedAddress.country}
                        </p>
                      </div>
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-start gap-2">
                    <AddressListDrawer
                      open={isAddressDrawerOpen}
                      onOpenChange={setIsAddressDrawerOpen}
                      addresses={addresses}
                      selectedAddressId={selectedAddress?.id || null}
                      isAddressMutating={isAddressMutating}
                      onSelect={selectAddress}
                      onEdit={openEditAddressDrawer}
                      onDelete={deleteAddress}
                      onAddNew={openAddAddressDrawer}
                    />

                    <Button
                      variant="default"
                      className="rounded-full"
                      onClick={openAddAddressDrawer}
                      disabled={isAddressMutating}
                    >
                      Add New
                    </Button>
                  </div>
                </div>
              )}
              {!showAddressLoader && !selectedAddress && (
                <>
                  <p className="text-sm text-muted-foreground">
                    No addresses found. Please add an address to proceed.
                  </p>
                  <Button
                    variant="default"
                    className="rounded-full"
                    onClick={openAddAddressDrawer}
                    disabled={isAddressMutating}
                  >
                    Add Address
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <AddressFormModal
            open={isAddressFormDrawerOpen}
            onOpenChange={setIsAddressFormDrawerOpen}
            editingAddressId={editingAddressId}
            addressForm={addressForm}
            isAddressMutating={isAddressMutating}
            onSubmit={submitAddress}
          />
        </section>

        {/* Price Details */}
        {items.length > 0 && (
          <section className="bg-linear-to-b from-white to-background px-4 py-4 sm:px-6 sm:py-5 md:px-8">
            <h3
              className={`px-1 text-xl font-semibold ${philosopher.className}`}
            >
              Price Details
            </h3>
            <div className={`mt-3 ${inter.className}`}>
              <div className={`p-1 space-y-3 text-sm`}>
                {/* <div className="space-y-3 text-sm">
                  <Row label="Total Product Price" value={`+ ${priceFormat(subtotal)}`} />
                  <Row label="Total Discounts" value={`- ${priceFormat(couponDiscount)}`} />
                  <Separator />
                  <Row label="Order Total" value={`${priceFormat(orderTotal)}`} bold />
                </div> */}
                <div className="flex items-center justify-between ">
                  <h3>Total Product Price</h3>
                  <p className="">+ {priceFormat(subtotal)}</p>
                </div>
                <div className="flex items-center justify-between ">
                  <h3>Shipping Charges</h3>
                  <p className="">+ {priceFormat(totalShippingCharge)}</p>
                </div>
                <div className="flex items-center justify-between text-[#1F9975]">
                  <h3 className="underline underline-offset-3">
                    Total Discounts
                  </h3>
                  <p className="">- {priceFormat(couponDiscount)}</p>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <h3>Order Total</h3>
                  <p className="">{priceFormat(orderTotal)}</p>
                </div>
              </div>
            </div>

            <div
              className={`mt-3 flex items-center justify-center rounded-md bg-[#E5F5E5] py-2 text-emerald-700 font-semibold ${inter.className}`}
            >
              <BadgePercent className="h-4 bg-[#E5F5E5]" />
              <p className="text-sm">
                You saved {priceFormat(totalSavings)} on this order
              </p>
            </div>

            {sellerGroups.length > 0 && (
              <p
                className={`mt-3 px-1 text-xs text-muted-foreground ${inter.className}`}
              >
                Shipping is calculated seller-wise by weight (seller charge per
                kg x total ordered weight).
              </p>
            )}
          </section>
        )}
      </div>

      {/* Sticky Bottom Bar */}
      {items.length > 0 ? (
        <Checkout
          items={items}
          selectedAddress={selectedAddress}
          couponCode={appliedCoupon?.code}
          eligibleQuantity={totalEligibleQuantity}
          eligibleUnitPrice={lowestEligibleUnitPrice}
          sellerDeliveryTypes={sellerDeliveryTypes}
          sellerRemarks={sellerRemarks}
          onOutOfStock={handleCheckoutOutOfStock}
        >
          {({ handleCheckout, isCheckoutLoading }) => (
            <div
              className={`sticky bottom-0 z-40 bg-white rounded-t-4xl border-t border-zinc-200/60 shadow-[0_-8px_20px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)] ${inter.className}`}
            >
              <div className="mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 md:px-8">
                <div>
                  <div className="text-xl font-semibold text-secondary sm:text-2xl">
                    {priceFormat(orderTotal)}
                  </div>
                  {/* <button className="text-sm text-primary font-semibold">
                  VIEW DETAILED PRICE
                </button> */}
                </div>
                <button
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-secondary px-6 py-2 text-base text-white disabled:opacity-50 sm:w-auto sm:px-8 sm:py-1 sm:text-xl"
                  disabled={
                    showCartLoader ||
                    items.length === 0 ||
                    !selectedAddress ||
                    isCheckoutLoading
                  }
                  onClick={handleCheckout}
                >
                  {isCheckoutLoading ? "Processing..." : "Continue"}
                  <ChevronsRight className="h-7 sm:h-10" />
                </button>
              </div>
            </div>
          )}
        </Checkout>
      ) : null}

      <Dialog
        open={isOutOfStockDialogOpen && checkoutOutOfStockItems.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            handleDismissOutOfStockDialog();
          } else {
            setIsOutOfStockDialogOpen(true);
          }
        }}
      >
        <DialogContent className="rounded-3xl p-4 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Move out of stock items?</DialogTitle>
            <DialogDescription>
              Some cart items are no longer available. Move them to your
              wishlist so you can check back later.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
            {checkoutOutOfStockItems.map((item) => {
              const variant = item.productVariant;
              const product = variant?.product;

              return (
                <div
                  key={item.id}
                  className="grid grid-cols-[auto_1fr] gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-2"
                >
                  <CustomImage
                    src={variant?.featuredImage || ""}
                    alt={product?.name || variant?.sku || "Cart item"}
                    className="h-14 w-14 rounded-xl object-cover"
                  />
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm font-semibold text-zinc-900">
                      {product?.name || variant?.sku || "Product"}
                    </p>
                    <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                      {variant ? getVariantLabel(variant) : "-"}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-rose-600">
                      Out of stock
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              disabled={isMovingOutOfStockItems}
              onClick={handleDismissOutOfStockDialog}
            >
              Not Now
            </Button>
            <Button
              type="button"
              className="rounded-full"
              disabled={isMovingOutOfStockItems}
              onClick={() => void moveItemsToWishlist(checkoutOutOfStockItems)}
            >
              {isMovingOutOfStockItems ? "Moving..." : "Move to Wishlist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
